import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import { useReferralsInfo } from 'api/referrals/useReferralsInfo'
import * as spriteIcons from 'assets/svg/sprite'
import { IconButton } from 'components/IconButton/IconButton'
import ChangeStateTabs from 'components/UI/ChangeStateTabs/ChangeStateTabs'
import { getLastPeriodFn } from 'features/AnalyticsPeriodDropdown/AnalyticsPeriodDropdown'
import BarChart from 'features/BarChart/BarChart'
import { getLink } from 'helpers/appLinks'
import { getAnalyticsChartData } from 'helpers/dataTransformations'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { getEarningsGraphData } from 'services/endpoints/earning'

import styles from './_Referrals.module.scss'
import { Summary } from './view/Summary'


const chartOptionToTranslateKey = {
    last7Days: 'last7Days',
    last30Days: 'last30Days',
    last12Months: 'last12Months',
    allTime: 'allTime'
}
export enum TABS {
    CREATORS = 'Creators',
    FANS = 'Fans'
}

export const Referrals = () => {
    const [chartOptionsOpen, setChartOptionsOpen] = useState<boolean>(false)
    const [selectedChartOption, setSelectedChartOption] =
            useState<keyof typeof chartOptionToTranslateKey>('last7Days')
    const [copied, setCopied] = useState(false);
    const [activeTab, setActiveTab] = useState<TABS>(TABS.CREATORS)
    const [chartOpen, setChartOpen] = useState<boolean>(false)
    const [totalSum, setTotalSum] = useState<number>(0)

    const { id } = useParams<{ id: string }>()
    const { t } = useTranslation()

    const { data: referralsInfoData, isFetching: fetchingReferralInfo } =
        useReferralsInfo()


/*     const { data: chartData, isFetching: fetchingChart } = useChart()
 */

/*     const mappedChartData = useMemo(() => {
        return (
            chartData?.chart.map((item, index) => ({
                x: chartData.date_range[index],
                y: item,
                date: chartData.date_range[index]
            })) ?? []
        )
    }, [chartData]) */
    const handleCopy = () => {
        const textToCopy = `${getLink.referrLink()}${referralsInfoData?.message?.code}`;
        navigator.clipboard.writeText(textToCopy).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000); // Reset "Copied!" after 2 seconds
        });
    };


    const { data: graphData, isFetching: isFetchingGraph } = useQuery<any>(
        ['salesAnalyticsGraph', selectedChartOption],
        async () => {
            let selectedPeriod
            switch (selectedChartOption) {
                case 'last12Months':
                    selectedPeriod = getLastPeriodFn(11, 'month')()
                    break
                case 'last30Days':
                    selectedPeriod = getLastPeriodFn(1, 'month')()
                    break
                case 'last7Days':
                    selectedPeriod = getLastPeriodFn(6, 'day')()
                    break
                    case 'allTime':
                       
                        selectedPeriod = { start: '', end: '' };
                    
                        break;
               
            }
            return await getAnalyticsChartData({
                fetchPromise: getEarningsGraphData({
                    dateFrom:selectedPeriod?.start ?? '',
                    dateTo: selectedPeriod?.end ?? '',
                    all_time: selectedChartOption === 'allTime' ? 'alltime' : '',
                }).then(res => {
                    setTotalSum(res?.chart?.reduce((acc: number, item: any) => acc + (item.sum || 0), 0))
                    return res
                })
            })
        },
        {
            enabled: !!chartOpen,
            keepPreviousData: true,
            refetchOnMount: false
        }
    )

    const changeSelectedChartOption = (
        chartOpt: keyof typeof chartOptionToTranslateKey
    ) => {
        setSelectedChartOption(chartOpt)
        setChartOptionsOpen(false)
    }
    return (
        <>
            {id === 'summary' && (
                <SummarySection>
                    <div className={styles['balance-container']}>
                        {!chartOpen ? (
                            <>
                                <div className='balance-text'>
                                    {t('Total Earned')}
                                </div>
                                <div className='balance-number'>
                                    <spriteIcons.IconDollar
                                        width='58'
                                        height='58'
                                        color='black'
                                    />
                                    <div>
                                        {fetchingReferralInfo
                                            ? '...'
                                            : referralsInfoData?.message
                                                ?.balance}
                                    </div>
                                    <span className='balance-number-currency-text'>
                                        (USD)
                                    </span>
                                </div>
                                <div className='balance-options'>
                                    {/* <div className='balance-payment'>
                                        {getLink.referrLink()}
                                        {!fetchingReferralInfo && (
                                            <span>
                                                {
                                                    referralsInfoData?.message
                                                        ?.code
                                                }
                                            </span>
                                        )}
                                    </div> */}
                                    <div className='balance-payment'>
                                        {getLink.referrLink()}
                                        {!fetchingReferralInfo && (
                                            <span>
                                                {
                                                    referralsInfoData?.message
                                                        ?.code
                                                }
                                            </span>
                                        )}


                                    </div>
                                    {/* Copy Button */}
                                    <div style={{ display: 'flex', alignItems: 'center', marginRight: '1rem', }}>
                                        <div style={{ width: '70px' }}>
                                            <button
                                                onClick={handleCopy}
                                                style={{
                                                    width: '100%',
                                                    backgroundColor: '#262a33', // Tailwind's blue-500
                                                    color: 'white',
                                                    padding: '16px 8px',

                                                    borderRadius: '1rem',
                                                    border: 'none',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {copied ? 'Copied!' : 'Copy'}
                                            </button>
                                        </div>
                                    </div>


                                    <IconButton
                                        icon={<spriteIcons.IconGraph />}
                                        size='big'
                                        type='black'
                                        clickFn={() => {
                                            setChartOpen(true)
                                        }}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className='balance-chart'>
                                <div className='balance-chart-options'>
                                    <div className='balance-chart-currency'>
                                        <div className='balance-number'>
                                            <spriteIcons.IconDollar
                                                width='40'
                                                height='40'
                                                color='black'
                                            />
                                            <div>{totalSum}</div>
                                            <span className='balance-number-currency-text'>
                                                <div>(USD)</div>
                                                <div className='balance-text'>
                                                    {t('overallRevenue')}
                                                </div>
                                            </span>
                                        </div>
                                    </div>
                                    <div
                            className='balance-chart-select'
                            onClick={() => {
                                setChartOptionsOpen(!chartOptionsOpen)
                            }}
                        >
                            <div className='balance-chart-selected'>
                                {t('by')}{' '}
                                <span>
                                    {t(
                                        chartOptionToTranslateKey[
                                            selectedChartOption
                                        ]
                                    )}
                                </span>
                            </div>
                            <div
                                className={`balance-chart-select-list ${
                                    chartOptionsOpen &&
                                    'balance-chart-select-list-open'
                                }`}
                            > 
                                <div
                                    className='balance-chart-select-option'
                                    onClick={() => {
                                      changeSelectedChartOption('allTime')
                                    }}
                        >
                                    {t('by')} <span>{t('allTime')}</span>
                                </div>
                                <div
                                    className='balance-chart-select-option'
                                    onClick={() => {
                                        changeSelectedChartOption('last7Days')
                                    }}
                                >
                                    {t('by')} <span>{t('last7Days')}</span>
                                </div>
                                <div
                                    className='balance-chart-select-option'
                                    onClick={() => {
                                        changeSelectedChartOption('last30Days')
                                    }}
                                >
                                    {t('by')} <span>{t('last30Days')}</span>
                                </div>
                                <div
                                    className='balance-chart-select-option'
                                    onClick={() => {
                                        changeSelectedChartOption(
                                            'last12Months'
                                        )
                                    }}
                                >
                                    {t('by')} <span>{t('last12Months')}</span>
                                </div>
                            </div>
                        </div>
                                    <div
                                        className='balance-chart-close'
                                        onClick={() => {
                                            setChartOpen(false)
                                        }}
                                    >
                                        <spriteIcons.IconClose />
                                    </div>
                                </div>
                                <BarChart
                        chartData={graphData}
                        datasetOptions={{
                            borderRadius: 15,
                            barThickness: 9
                        }}
                    />
                    {/* {isFetchingGraph && <Loader />} */}
                            </div>
                        )}
                    </div>

                    <WithHeaderSection
                        customClass={styles.summaryHeader}
                        headerSection={
                            <ChangeStateTabs
                                activeTab={activeTab}
                                tabs={Object.values(TABS).map(key => ({
                                    name: t(key),
                                    value: key
                                }))}
                                clickFn={val => setActiveTab(val as TABS)}
                                width='fit'
                            />
                        }
                    >
                        <Summary tab={activeTab} />
                    </WithHeaderSection>
                </SummarySection>
            )}
        </>
    )
}

const SummarySection = styled.div``
